/**
 * Small date/time helpers for logging, diagnostics, and wire-format checks (no locale-specific business rules).
 *
 * Consolidation: merge overlap with:
 * `rest-server/src/api/common/utils/time.ts` (`timeUtil`, e.g. `convertToMSSQLDateFormat` and offset helpers).
 * Then import from `@nova/utils` at rest-server call sites (today: `rest-server/src/api/games/terraforming/models/match-end.request.ts`).
 * Nest `rest-server/src/api/common/services/time.service.ts` duplicates similar offset math for DI—longer term it can
 * delegate to shared pure functions from here instead of maintaining two copies.
 */

/**
 * True if the trimmed string ends with an explicit UTC designator (`Z`/`z`) or a numeric offset (`+05:00`, `-0730`, etc.).
 */
export function dateTimeStringHasExplicitOffset(isoDateTimeLike: string): boolean {
  const t = isoDateTimeLike.trim();
  return /[zZ]$/.test(t) || /[+-]\d{2}:?\d{2}$/.test(t);
}

/**
 * Human-readable duration from a millisecond delta (sign is ignored; use wording at the call site for past vs future).
 * Examples: `1h 2m 3s`, `45s`, `0s`.
 */
export function formatDurationHmsFromMilliseconds(durationMs: number): string {
  const abs = Math.abs(durationMs);
  const totalSec = Math.floor(abs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts: string[] = [];
  if (h > 0) { parts.push(`${h}h`); }

  if (m > 0 || h > 0) { parts.push(`${m}m`); }
  parts.push(`${s}s`);
  return parts.join(' ');
}

/**
 * Clock-style countdown duration from a millisecond delta.
 * Uses M:SS below one hour and H:MM:SS at one hour or above.
 * Examples: `9:59`, `1:00:00`.
 */
export function formatCountdownClockFromMilliseconds(durationMs: number): string {
  const totalSeconds = Math.ceil(Math.max(0, durationMs) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => value.toString().padStart(2, '0');

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

/**
 * One line: local wall time and the same instant as ISO UTC, for logs.
 * Invalid input yields `(invalid date: …)` with the original string when `value` was a string.
 */
export function formatDateForLogLocalAndUtc(value: string | Date): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) {
    const raw = typeof value === 'string' ? value : '(Date)';
    return `(invalid date: ${raw})`;
  }
  const local = d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  return `${local} (local) · ${d.toISOString()} (UTC)`;
}

/**
 * If `isoLike` has no trailing zone, append `Z` so `Date` parses it as UTC (handy when comparing naive wire strings).
 * Otherwise returns the trimmed string unchanged.
 */
export function appendUtcZIfNoExplicitOffset(isoLike: string): string {
  const t = isoLike.trim();
  if (dateTimeStringHasExplicitOffset(t)) { return t; }
  return `${t}Z`;
}
